import { useEffect, useState } from "react";
import { api } from "../../api";
import { jobLabel, PageHead, relative } from "../../components/common";
import { EmptyState, ProgressBar, StatusBadge } from "../../components/ui";
import type { Job } from "../../types";

export function JobsView() {
  const [jobs, setJobs] = useState<Job[]>([]);
  useEffect(() => {
    const load = () => api<Job[]>("/jobs").then(setJobs);
    void load();
    const id = setInterval(load, 3000);
    return () => clearInterval(id);
  }, []);
  return <>
    <PageHead eyebrow="FILA PERSISTENTE" title="Atividades" description="Acompanhe instalações, backups e mudanças longas mesmo após atualizar a página."/>
    <section className="panel">
      <div className="table-head"><span>Operação</span><span>Estado</span><span>Progresso</span><span>Iniciada</span></div>
      {jobs.map((job) => <div className="table-row" key={job.id}>
        <div className="job-operation">
          <b>{jobLabel(job.kind)}</b>
          <small>{job.message}</small>
          {job.status === "failed" && <JobFailureDetails job={job}/>}
        </div>
        <span><StatusBadge status={job.status === "succeeded" ? "success" : job.status === "failed" ? "failed" : "pending"} label={statusName(job.status)}/></span>
        <span><ProgressBar value={job.progress} label={`${jobLabel(job.kind)}: ${job.progress}%`}/></span>
        <span>{relative(job.createdAt)}</span>
      </div>)}
      {!jobs.length && <EmptyState kind="backups" compact title="Nenhuma atividade registrada" description="Instalações, backups e atualizações aparecerão aqui."/>}
    </section>
  </>;
}

function JobFailureDetails({ job }: { job: Job }) {
  const details = job.details?.trim() || "A operação falhou sem fornecer uma mensagem técnica.";
  return <details className="job-failure-details">
    <summary>Ver detalhes da falha</summary>
    <dl>
      <div><dt>Código</dt><dd>{job.errorCode || "não informado"}</dd></div>
      <div><dt>Operação</dt><dd>{job.id}</dd></div>
    </dl>
    <pre><code>{details}</code></pre>
  </details>;
}

export function AuditView() {
  const [rows, setRows] = useState<Array<Record<string, string | number>>>([]);
  useEffect(() => { void api<typeof rows>("/audit").then(setRows); }, []);
  return <>
    <PageHead eyebrow="SEGURANÇA" title="Auditoria" description="Ações críticas, resultados e origem — sem registrar segredos."/>
    <section className="panel">
      <div className="audit-filters" aria-label="Resumo dos filtros"><span>Registros mais recentes primeiro</span><small>IDs completos permanecem disponíveis nos detalhes técnicos.</small></div>
      <div className="table-head audit"><span>Ação</span><span>Usuário</span><span>Resultado</span><span>Quando</span></div>
      {rows.map((row) => <div className="table-row audit" key={String(row.id)}>
        <span><b>{String(row.action)}</b><small>{String(row.entityType)} · {String(row.entityId).slice(0, 8)}</small></span>
        <span>{String(row.user)}</span>
        <span><StatusBadge status={String(row.result) === "success" ? "success" : "failed"} label={String(row.result) === "success" ? "Sucesso" : "Falha"}/></span>
        <span>{relative(Number(row.createdAt))}</span>
      </div>)}
      {!rows.length && <EmptyState kind="backups" compact title="Nenhum evento de auditoria" description="Ações administrativas importantes serão registradas aqui."/>}
    </section>
  </>;
}

const statusName = (status: Job["status"]) => ({ queued: "Na fila", running: "Em andamento", succeeded: "Concluída", failed: "Falhou" })[status];
